/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { AxiosError } from 'axios';
import { OpenAPIObject } from 'openapi3-ts';

export interface SettingsInterface {
  enablePKCE: boolean;
  clientId: string;
  signInRedirectUrl: string;
  signOutRedirectUrl: string;
  clientHost: string;
  apimHost: string;
  apimApi: string;
  tokenExchangeUrl: string;
  apimGatewayHostSuffix: string;
  idpHost: string;
  graphqlServiceHost: string;
  graphqlResource: string;
  apiServiceHost: string;
  azureCDNOrigin: string;
  devToolEnable: boolean;
  scopes: string[];
  keyManager: string;
  devportalContext: string;
  apiServiceProxy: string;
  userInfo: string;
  userOrganizations: string;
  tokenEndpoint: string;
  revokeEndpoint: string;
  tokenValidityPeriod: number;
  defaultAppThrottlingPolicy: string;
  defaultAppTokenType: string;
  grantTypes: GrantType[];
  asgardeoGrantTypes: GrantType[];
  subscriptionThrottlingTiers: SubscriptionThrottlingTier[];
  tokenExchangeConfig: TokenExchangeConfiguration;
  enableInV2: boolean;
  choreoControlPlaneOrgHandleName: string;
  validateUserEndpoint: string;
  addEnterpriseUserEndpoint: string;
  googleFidp: string;
  githubFidp: string;
  microsoftFidp: string,
  emailFidp: string,
  enableMicrosoftLogin: boolean,
  enableEmailLogin: boolean,
  oldGoogleFidp: string;
  oldGithubFidp: string;
  enterpriseFidp: string;
  overrideEndpoints: Record<string, unknown>;
  wellKnownEndpoint: string;
  overrideWellEndpointConfig: boolean;
  azureInsightsKey: string;
  reactAppHotjarId: string;
  reactAppHotjarVersion : string;
  choreoSystemOrg: string;
  choreoSystemHostSuffix: string;
  asgardeoConsoleUrl: string;
  selfSignupEnabledOrgs: string[];
  pdpDomainCustomizedOrgs: string[];
  devportalHost: string,
  isCustomDomain: boolean,
  customDomain: string,
  validateIDToken: boolean,
  permissionAdminPortalEnabled: boolean,
  permissionAdminPortalUrl: string,
  permissionPortalOrgIdParam: string,
  sandboxKeyGenerationEnabled: boolean,
  intelligentRoutingFeatureEnabled: boolean,
  devPortalIdPConfigurationFeatureEnabled: boolean,
  apiTryoutTestKeyEnabled: boolean,
  customSubscriptionPolicyHandlingOrg: string,
  apiKeyFeatureEnabled: boolean,
}

export interface TokenExchangeConfiguration {
  clientId: string;
  grantType: string;
  subjectTokenType: string;
  requestedTokenType: string;
  scope: string;
}

export interface SubscriptionThrottlingTier {
  name: string;
  description: string;
}

export interface GrantType {
  id: number;
  type: string;
  value: string;
}

export interface ApplicationState {
  apiCommentsReducer: ApiCommentsReducer;
  apiReducer: ApiReducer;
  applicationReducer: ApplicationReducer;
  applicationKeysReducer: ApplicationKeyReducer;
  documentsReducer: DocumentsReducer;
  ratingsReducer: RatingsReducer;
  subscriptionsReducer: SubscriptionsReducer;
  swaggerReducer: SwaggerReducer;
  userReducer: UserReducer;
  credentialsReducer: CredentialsReducer;
  sdkReducer: SdkReducer;
  notificationReducer: NotificationReducer;
  apiTagsReducer: ApiTagsReducer;
  apiCategoriesReducer: ApiCategoriesReducer;
  settingsReducer: SettingsReducer;
  idpReducer: IDPReducer;
  customDomainOrganizationMappingReducer: CustomDomainOrganizationMappingReducer;
  organizationMetadataReducer: OrganizationMetadataReducer;
  subscriptionPoliciesReducer: SubscriptionPoliciesReducer;
  graphqlReducer: GraphqlReducer;
  addExternalUserToOrgReducer: AddExternalUserToOrgReducer;
  subscribedApiScopesReducer: SubscribedApiScopesReducer;
  apiKeysReducer: ApiKeysReducer;
}

export interface PolicyList {
  count: number;
  list: SubscriptionPolicy[];
  pagination: Pagination;
}

export interface SubscriptionPolicies {
  subscriptionPolicies: PolicyList;
  error: string;
  loading: boolean;
  success: boolean;
}

export enum RequestTimeUnit {
  Minute = "min",
  Hour = "hour",
  Day = "day",
}

export interface SubscriptionPolicy {
  name: string;
  description: string;
  displayName: string;
  requestCount: number;
  timeUnit: RequestTimeUnit;
  stopOnQuotaReach: boolean;
  rateLimitCount: number;
  rateLimitTimeUnit: RequestTimeUnit;
  attributes: {
    isApprovalRequired?: boolean
  }
}

export interface ApiCommentsReducer {
  comments: Comments;
  error: string;
  loading: boolean;
}

export interface CustomDomainOrganizationMappingReducer {
  customDomainOrganizationMapping: CustomDomainOrganizationMapping;
  error: string;
  loading: boolean;
  success: boolean;
}

export interface ApiKeysReducer {
  apiKey: ApiKeyV2WithKey;
  apiKeys : ApiKeyV2[];
  listApiKeyerror: string;
  generateApiKeyerror: string;
  isLoading: boolean;

  isKeyRegenerating: boolean;
  regeneratedApiKey: ApiKeyV2WithKey;
  regenerateApiKeyerror: string;

  isKeyDeleting: boolean;
  deleteApiKeyError: string;
}

export interface SubscriptionPoliciesReducer {
  subscriptionPolicies: PolicyList
  error: string;
  loading: boolean;
  success: boolean;
}

export interface OrganizationMetadataReducer {
  organizationMetadata: OrganizationMetadata;
  error: OrgMetaDataError;
  loading: boolean;
  success: boolean;
}

export interface GraphqlReducer {
  graphqlInfo: GraphqlResponse;
  error: string;
  loading: boolean;
}

export interface ToSubscribeAttributes {
  api?: string,
  apiId?: string,
  version?: string,
  policy?: string,
  availablePolicies?: string[],
  hasSubscribed?: boolean
}

export interface ApiReducer {
  api: Api;
  apiList: ApiList;
  featuredApiList: ApiList;
  featuredApiListLoading: boolean;
  majorVersionApiList: ApiList;
  majorVersionApisLoading: boolean;
  apiThumbnail: Blob;
  thumbnailMap: { [key: string]: string };
  error: string;
  loading: boolean;
  apiThumbnailLoading: boolean;
  headers: ApiRequestHeader;
}

export interface ApplicationReducer {
  applications: ApplicationList; 
  isLoading: boolean;
  error: string;
}

export interface ApplicationKeyReducer {
  currentKey: Key;
  keys: KeyList;
  error: string;
  isLoading: boolean;
  oauthToken: OauthToken;
  isTokenGenerating: boolean;
  isTokenGenerationSuccess: boolean;
  apiKey: ApiKey;
  isApikeyGenerating: boolean;
  isApikeyGenerationSuccess: boolean;
  isKeyMappingSuccess: boolean;
  isKeyMapping: boolean;
  isKeyUpdating: boolean;
  tokenScopes: string[];
}

export interface DocumentsReducer {
  documents: Documents;
  error: string;
  loading: boolean;
}

export interface RatingsReducer {
  ratings: ApiRatings;
  error: string;
  loading: boolean;
}

export interface SubscriptionsReducer {
  subscriptions: SubscriptionList;
  error: string;
  isLoading: boolean;
  isDeletingSubscription: boolean;
  fetchSuccess: boolean;
  updateSuccess: boolean;
}

export interface SwaggerReducer {
  swagger: OpenAPIObject;
  error: string;
  loading: boolean;
}

export interface ThrottlingTierList {
  count: number;
  list: SubscriptionThrottlingTier[];
  pagination: Pagination;
}

export interface ApplicationAttribute {
  description: string;
  required: string;
  attribute: string;
  hidden: string;
}

export interface ApplicationAttributes {
  count: number;
  list: ApplicationAttribute[];
}

export interface SettingsReducer {
  appAttributeList: ApplicationAttributes;
  error: string;
  loading: boolean;
}

export interface UserReducer {
  user: User;
  userInfo: { [key: string]: string };
  orgs: Organization[];
  signedIn: boolean;
  choreoTokenObtained: boolean;
  apimTokenObtained: boolean;
  apimTokenObtaining: boolean;
  requireLogin: boolean;
  error: AxiosError | string;
  loading: boolean;
  selectedOrg: Organization;
  status: number;
}

export interface CredentialsReducer {
  isKeyFetching: boolean;
  isKeysFetched: boolean;
  isKeyGenerating: boolean;
  isKeyDeleting: boolean;
  isTokenGenerating: boolean;
  keys: KeyList | null;
  oauthToken: OauthToken;
  error: string;
}

export interface SdkReducer {
  loading: boolean;
  languages: string[];
  error: string;
}

export interface NotificationReducer {
  type: Notificationtype;
  message: string;
}

export interface ApiTagsReducer {
  loading: boolean;
  tagList: ApiTagList;
  error: string;
}

export interface ApiCategoriesReducer {
  loading: boolean;
  categoryList: ApiCategoryList;
  error: string;
}

export interface SubscribedApiScopesReducer {
  loading: boolean;
  scopes: string[];
  error: string;
}

export enum Notificationtype {
  default = 'default',
  success = 'success',
  error = 'error',
  warning = 'warning',
  info = 'info',
}

export interface User {
  allowedScopes: string;
  authorizationEndpoint: string;
  displayName: string;
  email: string;
  oidcSessionIframe: string;
  tenantDomain: string;
  username: string;
}

export interface Organization {
  id: string;
  uuid: string;
  handle: string;
  name: string;
}

export interface Comments {
  count: number;
  list: Comment[];
  pagination: null;
}

export interface Comment {
  id: string;
  content: string;
  createdTime: string;
  createdBy: string;
  updatedTime: string;
}

export interface CreateComment {
  content: string;
}

export interface Tier {
  tierName: string;
  tierPlan: string;
  monetizationAttributes: string;
}

export interface URLs {
  http: string;
  https: string;
  ws: string;
  wss: string;
}

export interface EndpointURL {
  environmentName: string;
  environmentType: string;
  environmentDisplayName: string;
  choreoEnvironmentName: string;
  accessibilityType: string;
  isSandboxEndpoint: boolean;
  isCritical: boolean;
  isSandbox: boolean;
  URLs: URLs;
  defaultVersionURLs: URLs;
  deployedApiVersion?: string;
  deployedApiId?: string;
}

export interface BusinessInformation {
  businessOwner: string;
  businessOwnerEmail: string;
  technicalOwner: string;
  technicalOwnerEmail: string;
}

export interface AdvertiseInfo {
  advertised: boolean;
  originalStoreUrl: string;
  apiOwner: string;
  apiExternalProductionEndpoint?: string;
  apiExternalSandboxEndpoint?: string;
}

export interface ApiScope {
  key: string;
  name: string;
  roles: string[];
  description: string;
}

export interface AdditionalProperty {
  name: string;
  value: string;
  display: string;
}

export interface Api {
  id: string;
  name: string;
  displayName: string;
  description: string;
  context: string;
  version: string;
  provider: string;
  apiDefinition: string;
  wsdlUri: string;
  lifeCycleStatus: string;
  isDefaultVersion: boolean;
  type: string;
  transport: string[];
  operations: string[];
  authorizationHeader: string;
  apiKeyHeader: string;
  securityScheme: string[];
  tags: string[];
  tiers: Tier[];
  hasThumbnail: boolean;
  thumbnailUri: string;
  additionalProperties: Array<AdditionalProperty>;
  monetization: Record<string, unknown>;
  ingressURLs: string[];
  endpointURLs: EndpointURL[];
  businessInformation: BusinessInformation;
  labels: string[];
  environmentList: string[];
  scopes: ApiScope[];
  avgRating: string;
  advertiseInfo: AdvertiseInfo;
  isSubscriptionAvailable: boolean;
  categories: string[];
  keyManagers: string[];
  throttlingPolicies?: string[];
  versionInfo?: VersionInfo;
}

export interface VersionInfo {
  version: string;
  majorRange: MajorRangeVersionInfo;
  majorVersionEndpointUrls: EndpointURL[];
}

export interface MajorRangeVersionInfo {
  isLatest: boolean;
  latestVersion: string;
  latestVersionAPIId: string;
}

export interface ApiList {
  count: number;
  list: Api[];
  pagination: Pagination;
}

export interface ApiTag {
  value: string;
  count: number;
}

export interface ApiTagList {
  count: number;
  list: ApiTag[];
  pagination: Pagination;
}

export interface ApiCategory {
  id: string;
  name: string;
  description: string;
}

export interface ApiCategoryList {
  count: number;
  list: ApiCategory[];
}

export interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

export enum StorageOperations {
  ADD = 'ADD',
  GET = 'GET',
  REMOVE = 'REMOVE',
}

export interface Pagination {
  offset: number;
  limit: number;
  total: number;
  next: string;
  previous: string;
}

export interface ApiRating {
  ratingId: string;
  apiId: string;
  ratedBy: string;
  rating: number;
}

export interface ApiRatings {
  avgRating: string;
  userRating: number;
  count: number;
  list: ApiRating[];
  pagination: Pagination;
}

export interface Document {
  documentId: string;
  name: string;
  type: string;
  summary: string;
  sourceType: string;
  sourceUrl: string;
  otherTypeName: string;
}

export interface Documents {
  count: number;
  list: Document[];
  pagination: Pagination;
}

export interface ApiList {
  count: number;
  list: Api[];
  pagination: Pagination;
}

export interface ApiRequestHeader {
  headerName: string;
  headerValue: string;
}

export enum KeyType {
  PRODUCTION = 'PRODUCTION',
  SANDBOX = 'SANDBOX',
}

export enum TestKeyType {
  PRODUCTION = 'PRODUCTION',
  SANDBOX = 'SANDBOX',
}

export interface Subscription {
  subscriptionId: string;
  applicationId: string;
  apiId: string;
  apiInfo?: SubscriptionApiInfo;
  applicationInfo?: Application;
  throttlingPolicy: string;
  requestedThrottlingPolicy: string;
  status: string;
  redirectionParams?: string;
  versionRange?: string;
  createdTime?: string;
}
type Status = {
  ingress: string;
};

export interface CustomDomainListItem {
  urls: string[];
  environment: string;
  environmentDisplayName: string;
}

export interface CustomDomainOrganizationMapping {
  domain_name: string;
  organization: CustomDomainOrgInfo;
}

export interface OrganizationMetadata {
  handle: string;
  uuid: string;
}

export interface OrgMetaDataError {
  message?: string;
}

export interface CustomDomainOrgInfo {
  handle: string;
}

export interface GraphqlResponse {
  environments: Environment[];
}

export interface Environment {
  organizationUuid: string;
  orgShared: boolean;
  name: string;
  externalApiEnvName: string;
  internalApiEnvName?: string;
  sandboxApiEnvName?: string;
  description: string;
  id: string;
  vhost: string;
  choreoEnv: string;
  projectId: string;
  promoteFrom: string[];
  namespace: string;
  templateId: string;
}

export interface Comments {
  count: number;
  list: Comment[];
  pagination: null;
}

export interface SubscriptionApiInfo {
  id: string;
  name: string;
  displayName: string;
  description: string;
  context: string;
  version: string;
  provider: string;
  lifeCycleStatus: string;
  thumbnailUri: string;
  avgRating: string;
  throttlingPolicies: string[];
  advertiseInfo: AdvertiseInfo;
  businessInformation: BusinessInformation;
  isSubscriptionAvailable: boolean;
  monetizationLabel: string;
  versionInfo?: VersionInfo;
}

export interface SubscriptionList {
  count: number;
  list: Subscription[];
  pagination: Pagination | null;
}

export interface ApplicationOwnerInformation {
  id: string;
  displayName: string;
  email: string;
}

export interface Application {
  applicationId?: string;
  name: string;
  throttlingPolicy: string;
  description?: string;
  tokenType?: string;
  status?: string;
  groups?: string[];
  subscriptionCount?: number;
  keys?: Key[];
  attributes?: Record<string, unknown>;
  subscriptionScopes?: SubscriptionScope[];
  owner?: string;
  ownerInfo?: ApplicationOwnerInformation;
  hashEnabled?: boolean;
  createdTime: string;
  sharedWith?: UserPermission[];
  sharedPermissions?: string[];
}

export interface SubscriptionScope {
  key: string;
  name: string;
  roles: string[];
  description?: string;
}

export interface ApplicationList {
  count: number;
  list: Application[];
  pagination: Pagination;
}

export interface Key {
  keyMappingId: string;
  keyManager: string;
  consumerKey: string;
  consumerSecret: string;
  supportedGrantTypes: string[];
  callbackUrl: string;
  keyState: string;
  keyType: KeyType;
  groupId: number;
  token: Token;
  additionalProperties?: Record<string, unknown>;
  mode?: string;
}

export interface KeyList {
  count: number;
  list: Key[];
}

export interface Token {
  accessToken: string;
  tokenScopes: string[];
  validityTime: number;
}

export interface ApiKeyAdditionalProperties {
  permittedIp: string;
  permittedReferer: string;
}

export interface OauthToken {
  accessToken: string;
  tokenScopes: string[];
  validityTime: number;
}

export interface ApiKey {
  apikey: string;
  validityTime: number;
}

export interface ApplicationKeyMap {
  consumerKey: string;
  keyType: string;
  keyManager: string;
}

export interface IDP {
  id: string;
  name: string;
  type: string;
  tokenEndpoint: string;
  revokeEndpoint: string;
  authorizeEndpoint?: string;
  alias: string;
  enabled: boolean;
  tokenType?: string;
}

export interface IDPs {
  count: number;
  list: IDP[];
}

export interface IDPReducer {
  idps: IDPs;
  isLoading: boolean;
  error: string;
}

export interface AddExternalUserToOrgReducer {
  loading: boolean;
  error: string;
}
export interface EndpointInfo {
  url: string;
  environment: string;
  environmentDisplayName: string;
  choreoEnvironmentName: string;
  accessibilityType: string;
  isSandboxEndpoint: boolean;
  isCritical: boolean;
  isSandbox: boolean
}

export interface ChoreoUserInfo {
  createdAt: string;
  displayName: string;
  email: string;
  id: string;
  idpId: string;
  pictureUrl: string;
}

export interface ApplicationKeyMapping {
  consumerKey: string;
  keyType: string;
  keyManager: string;
}

export enum IdpRegisterType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
  EXCHANGE = 'EXCHANGE',
}

export interface UserPermission {
  userEmail: string;
  permissions?: string[];
}

export interface ApplicationShare {
  applicationId: string;
  userPermissions: UserPermission[];
}

export enum DevPortalAppEnv {
  PROD = 'prod',
  SANDBOX = 'sandbox',
}

export interface ApiKeyV2 {
  alias: string;
  application: {
    id: string;
    name: string;
    owner: {
      id: string;
      name: string;
      email: string;
    };
  };
  keys: {
    id: string;
    keyType: string;
  }[];
  scopes: string[];
  api: {
    id: string;
    name: string;
  };
}

export interface ApiKeyGenerationRequest {
  name: string;
  apiId: string;
  environmentTemplateId: string;
  applicationId: string;
  subscriptionPlan: string;
  scopes: string[];
  keyType: string;
}

export interface ApiKeyRegenerationRequest {
  apiKeyId: string;
}

export interface ApiKeyV2WithKey {
  id: string;
  value: string;
  keyType: string;
}
